import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  FormRow,
  Select,
  Checkbox,
  DescriptionList,
  Tooltip,
  IconButton
} from '@efast_public/fjd-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import {
  AddVerfahrenFromMarketplaceRequest,
  CreatedOnlineServiceResponse,
  OnlinedienstTemplate,
  PartnerWithStartApp,
  ServiceTemplatesResponse
} from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { useNavigate } from 'react-router-dom';
import { RouteKey } from '../pages/AppRoutes';
import { usePartnerFetcher } from '../api/hooks/usePartnerFetcher';

interface AddOnlinedienstFromMarketplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedService: OnlinedienstTemplate | null;
}

type FormData = {
  zustaendigeStelle: string;
  avvConfirmed: boolean;
  serviceTemplateId: string;
};

export function AddOnlinedienstFromMarketplaceModal({
  isOpen,
  onClose,
  selectedService
}: AddOnlinedienstFromMarketplaceModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();

  const { data: partners } = usePartnerFetcher();

  const isPrivate = selectedService?.accessType === 'private';
  const allowedZS = selectedService?.allowedZS as number[] | undefined;

  const allowedPartners: PartnerWithStartApp[] =
    isPrivate && allowedZS && allowedZS.length > 0
      ? partners.filter((partner) => allowedZS.includes(partner.appiid))
      : partners;
  const CustomJoi = createJoiWithTranslations(t);

  const { control, handleSubmit, watch } = useForm<FormData>({
    defaultValues: {
      zustaendigeStelle: '',
      avvConfirmed: false,
      serviceTemplateId: ''
    },
    resolver: joiResolver(
      CustomJoi.object<FormData>({
        zustaendigeStelle: CustomJoi.string().required(),
        avvConfirmed: CustomJoi.boolean().required(),
        serviceTemplateId: CustomJoi.string().allow('').optional()
      })
    ),
    mode: 'onBlur'
  });

  const { data: templates } = useQuery<ServiceTemplatesResponse>({
    queryKey: [
      GovosApiRoutes.serviceTemplates(partners?.at(0)?.appiid ?? 0, 'total')
    ],
    enabled: partners?.length > 0
  });

  const formValues = watch();

  const { mutateAsync: addVerfahrenFromMarketplace } = useMutation({
    mutationFn: async ({
      payload
    }: {
      payload: AddVerfahrenFromMarketplaceRequest;
    }) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.configuredOnlinedienste(), payload),
        {
          successMessage: t('api.successMessage.createOnlineDienst'),
          errorMessage: t('api.errorMessage.createOnlineDienst'),
          loadingMessage: t('api.loadingMessage.createOnlineDienst')
        }
      ),
    onSuccess: () => {
      onClose();
    }
  });

  const handleAdd = handleSubmit((data) => {
    if (selectedService) {
      addVerfahrenFromMarketplace({
        payload: {
          marketplaceId: selectedService.id,
          version: selectedService.version,
          title: selectedService.title,
          serviceTemplateId: data.serviceTemplateId,
          startAppId: data.zustaendigeStelle
        }
      }).then((response) => {
        const responseVerfahren =
          response as unknown as CreatedOnlineServiceResponse;
        const { processId } = responseVerfahren.verfahren;
        queryClient
          .invalidateQueries({
            queryKey: [GovosApiRoutes.configuredOnlinedienste()]
          })
          .then(() => {
            navigate(RouteKey.MANAGED_ONLINEDIENSTE + '/' + processId);
          });
      });
    }
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('AddOnlinedienstFromMarketplaceModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <DescriptionList hasDivider>
            <DescriptionList.Item
              title={t('AddOnlinedienstFromMarketplaceModal.onlineDienstLabel')}
              description={selectedService?.title || '-'}
            />
            <DescriptionList.Item
              title={t(
                'AddOnlinedienstFromMarketplaceModal.zustaendigeStelleLabel'
              )}
              description={
                <Controller
                  name="zustaendigeStelle"
                  control={control}
                  render={({ field }) => (
                    <Select
                      canSearch
                      value={field.value}
                      onChange={(value) => field.onChange(value.at(0)?.value)}
                      onBlur={field.onBlur}
                      name={field.name}
                      options={allowedPartners.map((partner) => ({
                        value: String(partner.appiid),
                        subline: partner.startApp.name,
                        label: String(partner.dispname ?? '-')
                      }))}
                      placeholder={
                        allowedPartners.length === 0
                          ? t('select.noOptionsAvailable')
                          : t(
                              'AddOnlinedienstFromMarketplaceModal.zustaendigeStelleePlaceholder'
                            )
                      }
                      disabled={allowedPartners.length === 0}
                      style={{ width: '100%' }}
                    />
                  )}
                />
              }
            />
          </DescriptionList>

          <FormRow
            label={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Text>
                  {t(
                    'AddOnlinedienstFromMarketplaceModal.serviceTemplateLabel'
                  )}
                </Text>
                <Tooltip
                  tooltip={t(
                    'AddOnlinedienstFromMarketplaceModal.serviceTemplateLabelHint'
                  )}
                >
                  <IconButton
                    variant="primary-text"
                    label=""
                    icon="information-filled"
                    size="sm"
                  />
                </Tooltip>
              </div>
            }
          >
            <Controller
              name="serviceTemplateId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(value) => field.onChange(value.at(0)?.value ?? '')}
                  onBlur={field.onBlur}
                  name={field.name}
                  options={
                    templates?.serviceTemplates?.map((template) => ({
                      value: String(template.id),
                      label: String(template.name)
                    })) ?? []
                  }
                  placeholder={
                    templates?.serviceTemplates?.length === 0
                      ? t(
                          'AddOnlinedienstFromMarketplaceModal.serviceTemplatePlaceholder'
                        )
                      : t(
                          'AddOnlinedienstFromMarketplaceModal.selectServiceTemplatePlaceholder'
                        )
                  }
                  disabled={templates?.serviceTemplates?.length === 0}
                  style={{ width: '100%' }}
                />
              )}
            />
          </FormRow>

          <div>
            <Text
              size="s"
              strong
              style={{ marginBottom: '8px', display: 'block' }}
            >
              Bitte beachten Sie:
            </Text>
            <Text size="s" style={{ marginBottom: '12px', display: 'block' }}>
              {t('AddOnlinedienstFromMarketplaceModal.avvNotice')}
            </Text>
            <Controller
              name="avvConfirmed"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(checked) => field.onChange(checked)}
                  label={t('AddOnlinedienstFromMarketplaceModal.avvCheckbox')}
                />
              )}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => void handleAdd()}
            disabled={
              !(formValues.zustaendigeStelle && formValues.avvConfirmed)
            }
            style={{ width: '100%' }}
          >
            {t('AddOnlinedienstFromMarketplaceModal.addButton')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            style={{ width: '100%' }}
          >
            {t('AddOnlinedienstFromMarketplaceModal.cancelButton')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
